'use client'

import { useState } from 'react'

import { ChallengeIcon, ManageBookIcon } from '@ui/web/icon'
import { typographyVariants } from '@ui/web/lib'
import { formatDate } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type { DateRangeValue } from '../../../lib'
import type { ChildProfileOptions } from '../index'
import type { RenderToLessonModuleLink } from './lesson-history'
import { DateRangeSelect, normalizeDateRange } from '../../../lib'
import { LessonHistory } from './lesson-history'
import { LessonOverview } from './lesson-overview'
import { ReadingHistory } from './reading-history'
import { ReadingOverview } from './reading-overview'

export type ActivityView = 'reading' | 'lesson'

interface ActivityTabProps {
  childId: string
  className?: string
  options: ChildProfileOptions
  view?: ActivityView
  onViewChange?: (view: ActivityView) => void
  renderToLessonModuleLink?: RenderToLessonModuleLink
}

// Pill toggle styling mirrors the dashboard trend filter header (ui/shared/src/lib/dashboard-tabs/trend-filter-header.tsx).
const activityPillClassName = cn(
  typographyVariants({ variant: 'label-md' }),
  'srd:inline-flex srd:h-auto srd:flex-none srd:items-center srd:gap-2 srd:rounded-xl srd:border srd:border-blue-70 srd:px-4 srd:py-2',
  'srd:text-grey-80 srd:transition-colors srd:hover:text-grey-100',
)

const activityPillActiveClassName = 'srd:bg-blue-30 srd:border-blue-30 srd:text-navy-80 srd:hover:text-navy-80'

export function ActivityTab({ childId, className, view, onViewChange, renderToLessonModuleLink }: ActivityTabProps) {
  const [internalView, setInternalView] = useState<ActivityView>('reading')
  const currentView = view ?? internalView
  const setView = (next: ActivityView) => {
    if (view === undefined) setInternalView(next)
    onViewChange?.(next)
  }

  // Resolved range driving the reading overview metrics (the reading view consumes from/to).
  const [dateRange, setDateRange] = useState<DateRangeValue>(() => {
    const from = new Date()
    from.setMonth(from.getMonth() - 1)
    return normalizeDateRange(from, new Date())
  })

  return (
    <div className={cn('srd:mx-auto srd:flex srd:w-full srd:max-w-[1200px] srd:flex-col srd:gap-6', className)}>
      <div className='srd:flex srd:w-full srd:flex-col srd:gap-4'>
        <div className='srd:flex srd:flex-wrap srd:items-center srd:justify-start srd:gap-4'>
          <h6 className={cn(typographyVariants({ variant: 'title-md' }))}>Aktivitas</h6>
          <div className='srd:flex srd:flex-wrap srd:items-center srd:justify-start srd:gap-2'>
            <button
              type='button'
              aria-pressed={currentView === 'reading'}
              onClick={() => setView('reading')}
              className={cn(activityPillClassName, currentView === 'reading' && activityPillActiveClassName)}
            >
              <ManageBookIcon className='srd:size-5 srd:shrink-0' size={20} color='currentColor' />
              Membaca
            </button>
            <button
              type='button'
              aria-pressed={currentView === 'lesson'}
              onClick={() => setView('lesson')}
              className={cn(activityPillClassName, currentView === 'lesson' && activityPillActiveClassName)}
            >
              <ChallengeIcon className='srd:size-5 srd:shrink-0' size={20} color='currentColor' />
              Belajar
            </button>
          </div>
        </div>

        <div className='srd:flex srd:flex-wrap srd:items-center srd:gap-4'>
          <DateRangeSelect
            value={dateRange}
            onChange={setDateRange}
            size='default'
            className='srd:data-[size=default]:h-10 srd:w-full srd:max-w-[330px]'
          />
        </div>

        <p className={cn(typographyVariants({ variant: 'body-md' }))}>
          {formatDate(dateRange.from, { dateStyle: 'long' })} - {formatDate(dateRange.to, { dateStyle: 'long' })}
        </p>
      </div>

      <div className='srd:flex srd:w-full srd:min-w-0 srd:flex-col srd:gap-6'>
        {currentView === 'reading' ? (
          <>
            <ReadingOverview childId={childId} from={dateRange.from} to={dateRange.to} />
            <h6 className={cn(typographyVariants({ variant: 'title-md' }), 'srd:text-neutral-black')}>Riwayat baca</h6>
            <ReadingHistory childId={childId} from={dateRange.from} to={dateRange.to} />
          </>
        ) : (
          <>
            <LessonOverview childId={childId} from={dateRange.from} to={dateRange.to} />
            <h6 className={cn(typographyVariants({ variant: 'title-md' }), 'srd:text-neutral-black')}>
              Riwayat belajar
            </h6>
            <LessonHistory childId={childId} renderToLessonModuleLink={renderToLessonModuleLink} />
          </>
        )}
      </div>
    </div>
  )
}
